/* eslint-disable react/jsx-pascal-case */
/* eslint-disable no-underscore-dangle */
import { format, parse } from 'date-fns';
import _ from 'lodash';
import React, { memo, useCallback, useEffect, useRef, useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useImmerReducer } from 'use-immer';

import fs from 'app/native/node/fs';
import path from 'app/native/node/path';
import {
  selectXRayConfiguration,
  selectXRaySelectedOperators,
} from 'app/redux/xrayGeneratorConfiguration/reducer';
import {
  CONNECTION_KEEP_ALIVE_TIMEOUT_MS,
  CONNECTION_RETRY_INTERVAL_MS,
  GENERATOR_STATUS_REQUEST_STANDBY_INTERVAL_MS,
  GENERATOR_WAKE_UP_INTERVAL_MS,
} from 'app/xray/generator/constants';
import XRayGeneratorManagerContext from 'app/providers/XRayGeneratorManagerProvider/context';
import {
  EVENT_GENERATOR_RAD_PARAMETERS_CHANGE,
  EVENT_GENERATOR_STATUS,
  EVENT_SW_VER,
  EVENT_GENERATOR_RAD_PARAMETERS_EXPOSURE,
  EVENT_GENERATOR_PREP_STATE,
  EVENT_GENERATOR_EXPOSURE_STATE,
  EVENT_GENERATOR_THICKNESS,
  EVENT_GENERATOR_ERROR,
  EVENT_GENERATOR_WARNING,
  STATE_CHANGE,
  IGNORED_WARNINGS,
  GENERATOR_WARNING,
  UNIT_STATUS,
  IGNORED_WARNINGS_IN_HISTORY,
} from 'app/xray/generator/constants';
import { Message, Modal } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import logger from 'app/utils/debug/logger';
import convertXRayFileToShotObjects from './convertXRayFileToShotObjects';

const CONNECTION_STATE = ['not_connected', 'connection_failed', 'connecting', 'connected'];

/**
 * @typedef {Object} XRayGeneratorState
 * @prop {String} version
 * @prop {import("app/adapters/XRayGenerator/XRayGeneratorController").RadParameters} radParameters
 * @prop {import("app/adapters/XRayGenerator/XRayGeneratorController").UnitStatus} unitStatus
 * @prop {import("app/adapters/XRayGenerator/XRayGeneratorController").ReadyStatus} readyStatus
 * @prop {import("app/adapters/XRayGenerator/XRayGeneratorController").Connec} connectionState
 * @prop {import("app/adapters/XRayGenerator/XRayGeneratorController").GeneratorError | undefined} error
 * @prop {import("app/adapters/XRayGenerator/XRayGeneratorController").GeneratorWarning | undefined} warning
 * @prop {boolean} prepStarted
 */

/**
 * @typedef {Object} XRayGeneratorHistory
 * @prop {(operator : String|undefined) => String} getHistoryAsCsv
 * @prop {() => [Object]} getAllHistory
 */
/**
 * @typedef {Object} XRayGeneratorFaultsHistory
 * @prop {() => [Object]} getFaults
 */

/** @type {React.Context<XRayGeneratorController>} */
export const XRayGeneratorControllerContext = React.createContext();
/** @type {React.Context<XRayGeneratorState>} */
export const XRayGeneratorDispatchContext = React.createContext();
/** @type {React.Context<XRayGeneratorState>} */
export const XRayGeneratorStateContext = React.createContext();
/** @type {React.Context<XRayGeneratorHistory>} */
export const XRayGeneratorFaultsHistoryContext = React.createContext();

export const setConnectionState = (connectionState) => ({
  type: 'setConnectionState',
  payload: connectionState,
});
export const setSoftwareVersion = (softwareVersion) => ({
  type: 'setSoftwareVersion',
  payload: softwareVersion,
});
export const updateGeneratorStatus = (generatorStatus) => ({
  type: 'updateGeneratorStatus',
  payload: generatorStatus,
});
export const setErrorStatus = (errorCode) => ({
  type: 'setErrorStatus',
  payload: errorCode,
});
export const setWarningStatus = (warningCode) => ({
  type: 'setWarningStatus',
  payload: warningCode,
});
export const clearWarningStatus = () => ({
  type: 'clearWarningStatus',
});
export const setRadParameters = (radParameters) => ({
  type: 'setRadParameters',
  payload: radParameters,
});
export const setXRayOngoing = (isOngoing) => ({
  type: 'setXRayOngoing',
  payload: isOngoing,
});
export const startPreparation = () => ({
  type: 'startPreparation',
});
export const clearPreparation = () => ({
  type: 'clearPreparation',
});
export const setCooldownTime = (cooldownTime) => ({
  type: 'setCooldownTime',
  payload: { cooldownTime },
});

const xRayGeneratorStateReducer = (draft, { type, payload }) => {
  if (type === 'setConnectionState') {
    draft.connectionState = payload;
    if (draft.connectionState !== 'connected') {
      draft.softwareVersion = undefined;
      draft.unitStatus = undefined;
      draft.readyStatus = undefined;
      draft.error = undefined;
      draft.warning = undefined;
      draft.radParameters = undefined;
      draft.xRayOngoing = false;
      draft.prepStarted = false;
    }
  } else if (type === 'setSoftwareVersion') draft.softwareVersion = payload;
  else if (type === 'setErrorStatus') draft.error = payload;
  else if (type === 'setWarningStatus') draft.warning = payload;
  else if (type === 'clearWarningStatus') draft.warning = undefined;
  else if (type === 'setRadParameters') {
    draft.radParameters = _.pick(payload, ['kV', 'mAs', 'mA', 'ms', 'focus']);
  } else if (type === 'updateGeneratorStatus') {
    draft.unitStatus = payload.unitStatus;
    draft.readyStatus = payload.readyStatus;
    if (
      draft.unitStatus === UNIT_STATUS.FIRST_TIME ||
      draft.unitStatus === UNIT_STATUS.SYSTEM_ERROR
    ) {
      draft.prepStarted = false;
    }
    if (draft.unitStatus !== UNIT_STATUS.XRAY_IN_PROGRESS) {
      draft.xRayOngoing = false;
    }
    if (draft.unitStatus !== UNIT_STATUS.SYSTEM_ERROR) {
      draft.warning = undefined;
    }
  } else if (type === 'setXRayOngoing') {
    draft.xRayOngoing = payload;
  } else if (type === 'startPreparation') {
    draft.prepStarted = true;
  } else if (type === 'clearPreparation') {
    draft.prepStarted = false;
  } else if (type === 'setCooldownTime') {
    draft.cooldownTime = payload.cooldownTime;
  }
};

const CONSTANTS_KEYS = ['kV', 'mAs', 'mA', 'ms'];

const formatRadParamsForLog = (params) => {
  if (
    !params ||
    _.some(params, (value, key) => CONSTANTS_KEYS.includes(key) && value === undefined)
  ) {
    return '-';
  }
  return `${params.kV}|${params.mA}|${params.ms}|${params.mAs}`;
};

const formatRadResultForLog = (result) => {
  if (
    !result ||
    _.some(result, (value, key) => CONSTANTS_KEYS.includes(key) && value === undefined)
  ) {
    return '-';
  }
  return `${result.kV}|${result.mA}|${result.ms}|${result.mAs}`;
};

const saveErrorToHistory = async (xRayErrorLogPath, code, isError, radParameters, radResult) => {
  const nodeFs = fs();
  if (!nodeFs) return;
  if (!xRayErrorLogPath) return;

  const formattedDate = format(new Date(), 'yyyy-MM-dd_kk:mm:ss');
  const type = isError ? 'E' : 'W';
  const radParamsStr = formatRadParamsForLog(radParameters);
  const radResultStr = formatRadResultForLog(radResult);
  const lineContent = `v2 ${formattedDate} ${type} ${code} ${radParamsStr} ${radResultStr}`;
  await fs()?.promises.mkdir(path()?.dirname(xRayErrorLogPath), { recursive: true });
  await fs()?.promises.appendFile(xRayErrorLogPath, `${lineContent}\r\n`, {
    encoding: 'utf8',
  });
};

export const X_RAY_TUBE_NOT_READY_TIMER = 3000;
/**
 * @param {Object} param
 */
function XRayGeneratorProvider({ children, xRayErrorLogPath }) {
  const xRayGeneratorManager = useContext(XRayGeneratorManagerContext);
  if (!xRayGeneratorManager) return children;

  const xRayConfiguration = useSelector(selectXRayConfiguration);
  const [xRayGeneratorController, setXRayGeneratorController] = useState();
  const [xrayHandle, setXRayHandle] = useState();
  const xRayGeneratorControllerRef = useRef();
  xRayGeneratorControllerRef.current = xRayGeneratorController;
  const [xRayGeneratorState, dispatchConnectionState] = useImmerReducer(xRayGeneratorStateReducer, {
    connectionState: 'not_connected',
    softwareVersion: undefined,
    unitStatus: undefined,
    readyStatus: undefined,
    error: undefined,
    warning: undefined,
    radParameters: undefined,
    xRayOngoing: false,
    prepStarted: false,
    cooldownTime: 0,
  });
  const connectionWakeUpTimeout = useRef();
  const requestStatusTimeout = useRef();
  const connectionRetryTimeout = useRef();
  const monitorCooldownTimeTimeout = useRef();
  const generatorStatusRequestDelay = useRef(CONNECTION_KEEP_ALIVE_TIMEOUT_MS);
  const xRayErrorLogPathRef = useRef();
  xRayErrorLogPathRef.current = xRayErrorLogPath;

  const xRayGeneratorStateRef = useRef();
  xRayGeneratorStateRef.current = xRayGeneratorState;
  // const xRayGeneratorPrevStateRef = useRef();
  const [isBadQualityWarningShown, setIsBadQualityWarningShown] = useState(false);

  const parseRadParams = (paramsStr) => {
    if (!paramsStr || paramsStr === '-') return undefined;
    const [kV, mA, ms, mAs] = paramsStr.split('|').map(Number);
    return { kV, mA, ms, mAs };
  };

  const parseFaultLine = (errorLine) => {
    // v2 format: v2 {datetime} {type} {code} {radParams} {radResult}
    if (errorLine.startsWith('v2 ')) {
      const [, datetime, type, code, radParamsStr, radResultStr] = errorLine.split(' ');
      return {
        timestamp: parse(datetime, 'yyyy-MM-dd_kk:mm:ss', new Date()),
        type: type === 'E' ? 'error' : 'warning',
        code: parseInt(code, 10),
        radParams: parseRadParams(radParamsStr),
        resultParams: parseRadParams(radResultStr),
      };
    }
    // v1 format (legacy): {datetime} {type} {hexCode}
    const [datetime, type, code] = errorLine.split(' ');
    return {
      timestamp: parse(datetime, 'yyyy-MM-dd_kk:mm:ss', new Date()),
      type: type === 'E' ? 'error' : 'warning',
      code: parseInt(code, 16),
      radParams: undefined,
      resultParams: undefined,
    };
  };

  const xRayGeneratorDefaultHistory = useRef();
  xRayGeneratorDefaultHistory.current = {
    getFaults: async () =>
      fs()
        ?.promises.readFile(xRayErrorLogPathRef.current, { encoding: 'utf8' })
        .then((fileContent) => fileContent.split(/\r?\n/).filter(_.identity).map(parseFaultLine))
        .catch(() => []),
  };

  const { devicePath } = xRayConfiguration;

  const refreshKeepAlive = () => {
    const planRequestStatus = (lastRequest) => {
      clearTimeout(requestStatusTimeout.current);
      const timeoutId = setTimeout(() => {
        clearTimeout(requestStatusTimeout.current);
        if (!xRayGeneratorControllerRef.current) return;

        xRayGeneratorControllerRef.current.requestStatus();

        if (!lastRequest) {
          planRequestStatus(true);
          return;
        }

        requestStatusTimeout.current = setTimeout(
          () => dispatchConnectionState(setConnectionState('connecting')),
          CONNECTION_KEEP_ALIVE_TIMEOUT_MS
        );
      }, generatorStatusRequestDelay.current);

      requestStatusTimeout.current = timeoutId;
    };
    planRequestStatus(false);
  };

  const monitorCooldownTime = () => {
    if (xRayGeneratorState?.unitStatus !== UNIT_STATUS.STANDBY_MODE) {
      return;
    }
    console.debug('Retrieveing cooldown time');
    xRayGeneratorController
      ?.getRemainingCooldownTime()
      .then((cooldownTime) => {
        console.debug('coldownTime', cooldownTime);
        dispatchConnectionState(setCooldownTime(cooldownTime));

        if (cooldownTime !== 0) {
          clearTimeout(monitorCooldownTimeTimeout.current);
          monitorCooldownTimeTimeout.current = setTimeout(monitorCooldownTime, 1000);
        }
      })
      .catch(() => {
        console.debug('coldownTime error');
        clearTimeout(monitorCooldownTimeTimeout.current);
        monitorCooldownTimeTimeout.current = setTimeout(monitorCooldownTime, 1000);
      });
  };

  const connectToGenerator = useCallback(async () => {
    setXRayGeneratorController(undefined);
    if (!devicePath) {
      dispatchConnectionState(setConnectionState('not_connected'));
      return;
    }

    let newXRayGeneratorController;
    try {
      const xRayGeneratorHandle = await xRayGeneratorManager.get(devicePath);
      setXRayHandle(xRayGeneratorHandle);
      dispatchConnectionState(setConnectionState('connecting'));

      xRayGeneratorHandle.on('disconnected', () => {
        setXRayHandle(undefined);
      });

      newXRayGeneratorController = await xRayGeneratorHandle.connect(
        xRayConfiguration?.isDirectCommunicationMode
      );
    } catch (e) {
      setXRayHandle(undefined);
      logger.error(e);
      dispatchConnectionState(setConnectionState('connection_failed'));
      return;
    }

    setXRayGeneratorController(newXRayGeneratorController);

    newXRayGeneratorController.on(EVENT_SW_VER, ({ version, major, minor }) => {
      dispatchConnectionState(setSoftwareVersion({ version, major, minor }));
      dispatchConnectionState(setConnectionState('connected'));
      clearTimeout(connectionWakeUpTimeout.current);
      connectionWakeUpTimeout.current = undefined;
    });

    newXRayGeneratorController?.on(EVENT_GENERATOR_STATUS, (event) => {
      refreshKeepAlive();
      dispatchConnectionState(updateGeneratorStatus(event));
    });
    newXRayGeneratorController.on(EVENT_GENERATOR_RAD_PARAMETERS_CHANGE, (event) => {
      dispatchConnectionState(setRadParameters(event));
    });
    newXRayGeneratorController.on(EVENT_GENERATOR_RAD_PARAMETERS_EXPOSURE, () => {
      newXRayGeneratorController.requestStatus();
    });
    newXRayGeneratorController.on(
      EVENT_GENERATOR_WARNING,
      ({ warningCode, radParameters, radResult }) => {
        if (
          IGNORED_WARNINGS_IN_HISTORY.includes(warningCode) ||
          xRayGeneratorStateRef.current.warning === warningCode
        )
          return;
        saveErrorToHistory(
          xRayErrorLogPathRef.current,
          warningCode,
          false,
          radParameters,
          radResult
        ).catch((error) => console.error('Error saving warning to history', error));
      }
    );
    newXRayGeneratorController.on(
      EVENT_GENERATOR_ERROR,
      ({ errorCode, radParameters, radResult }) => {
        if (xRayGeneratorStateRef.current.error === errorCode) return;
        saveErrorToHistory(xRayErrorLogPathRef.current, errorCode, true, radParameters, radResult);
      }
    );
    newXRayGeneratorController.on(EVENT_GENERATOR_PREP_STATE, ({ status }) => {
      newXRayGeneratorController.prepConfirmation(status === STATE_CHANGE.ACTIVE);
      if (status === STATE_CHANGE.ACTIVE) {
        dispatchConnectionState(startPreparation());
      }
    });
    newXRayGeneratorController.on(EVENT_GENERATOR_EXPOSURE_STATE, ({ status }) => {
      newXRayGeneratorController.startXRay(status === STATE_CHANGE.ACTIVE);
      dispatchConnectionState(setXRayOngoing(status === STATE_CHANGE.ACTIVE));
    });
    newXRayGeneratorController.on(EVENT_GENERATOR_THICKNESS, () => {});
    newXRayGeneratorController.on(EVENT_GENERATOR_ERROR, (event) => {
      dispatchConnectionState(setErrorStatus(event.errorCode));
    });
    newXRayGeneratorController.on(EVENT_GENERATOR_WARNING, (event) => {
      if (IGNORED_WARNINGS.includes(event.warningCode)) {
        newXRayGeneratorController?.resetWarning();
        return;
      }

      dispatchConnectionState(setWarningStatus(event.warningCode));

      if (event.warningCode === GENERATOR_WARNING.XRAY_CANCEL_BY_OPERATOR) {
        setIsBadQualityWarningShown(true);
        setTimeout(() => setIsBadQualityWarningShown(false), 5000);
        setTimeout(() => newXRayGeneratorController?.resetWarning(), 2000);
      } else if (event.warningCode === GENERATOR_WARNING.X_RAY_TUBE_NOT_READY) {
        monitorCooldownTime();
        setTimeout(() => {
          newXRayGeneratorController?.resetWarning();
        }, X_RAY_TUBE_NOT_READY_TIMER);
      }
    });
  }, [devicePath, xRayGeneratorManager, xRayConfiguration?.isDirectCommunicationMode]);

  const autoClearPrepStarted = () => {
    if (!xRayGeneratorState?.prepStarted) return undefined;
    const clearPrepTimeout = setTimeout(() => dispatchConnectionState(clearPreparation()), 5000);
    return () => clearTimeout(clearPrepTimeout);
  };
  useEffect(autoClearPrepStarted, [
    xRayGeneratorState?.prepStarted,
    xRayGeneratorState?.unitStatus,
  ]);

  useEffect(monitorCooldownTime, [xRayGeneratorState?.unitStatus]);
  // Reconnect each time function change, ie each time devicePath, change
  useEffect(() => {
    connectToGenerator();
  }, [connectToGenerator, devicePath]);

  useEffect(() => {
    if (xrayHandle === undefined) {
      setXRayGeneratorController(undefined);
      if (devicePath) {
        connectionRetryTimeout.current = setTimeout(
          () => connectToGenerator(),
          CONNECTION_RETRY_INTERVAL_MS
        );
      }
    }
    return () => clearTimeout(connectionRetryTimeout.current);
  }, [connectToGenerator, devicePath, xrayHandle]);

  // Clear all timers on unmount
  useEffect(
    () => () => {
      clearTimeout(connectionWakeUpTimeout.current);
      clearTimeout(requestStatusTimeout.current);
      clearTimeout(connectionRetryTimeout.current);
    },
    []
  );

  // Close xRayGeneratorController each time is it recreated
  useEffect(() => {
    if (!xRayGeneratorController) return undefined;

    const shutdownBeforeUnload = () => {
      xRayGeneratorController?.shutdown();
      xRayGeneratorController?.close();
    };
    window.addEventListener('beforeunload', shutdownBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', shutdownBeforeUnload);
      xRayGeneratorController?.shutdown();
      xRayGeneratorController?.close();
    };
  }, [xRayGeneratorController]);

  useEffect(() => {
    if (!xRayGeneratorController) return undefined;
    if (xRayGeneratorState.connectionState === 'connecting') {
      xRayGeneratorController.init(new Date());
      connectionWakeUpTimeout.current = setInterval(() => {
        xRayGeneratorController.init(new Date());
      }, GENERATOR_WAKE_UP_INTERVAL_MS);
      return () => clearTimeout(connectionWakeUpTimeout.current);
    }
    if (xRayGeneratorState.connectionState !== 'connected') {
      return undefined;
    }
    xRayGeneratorController.requestStatus();
    refreshKeepAlive();

    return () => {
      clearTimeout(requestStatusTimeout.current);
    };
  }, [xRayGeneratorController, xRayGeneratorState.connectionState]);

  useEffect(() => {
    if (xRayGeneratorState?.connectionState !== 'connected') {
      return;
    }
    // if (xRayGeneratorPrevStateRef.current?.unitStatus === xRayGeneratorState.unitStatus) return;

    if (xRayGeneratorState.unitStatus === UNIT_STATUS.SLEEPING_MODE) {
      generatorStatusRequestDelay.current = CONNECTION_KEEP_ALIVE_TIMEOUT_MS;
      refreshKeepAlive();
    } else {
      generatorStatusRequestDelay.current = GENERATOR_STATUS_REQUEST_STANDBY_INTERVAL_MS;
      refreshKeepAlive();
    }
  }, [xRayGeneratorState?.unitStatus, xRayGeneratorState?.connectionState]);

  // useEffect(
  //   () => () => {
  //     xRayGeneratorPrevStateRef.current = xRayGeneratorState;
  //   },
  //   [xRayGeneratorState]
  // );

  return (
    <XRayGeneratorControllerContext.Provider
      value={
        xRayGeneratorState.connectionState === 'connected' ? xRayGeneratorController : undefined
      }
    >
      <XRayGeneratorDispatchContext.Provider value={dispatchConnectionState}>
        <XRayGeneratorStateContext.Provider value={xRayGeneratorState}>
          <XRayGeneratorFaultsHistoryContext.Provider value={xRayGeneratorDefaultHistory.current}>
            {children}
            <Modal
              open={isBadQualityWarningShown}
              onClose={() => setIsBadQualityWarningShown(false)}
              closeOnDimmerClick
            >
              <Message negative>
                <Message.Header
                  style={{
                    fontSize: '2em',
                    lineHeight: '1.2em',
                    padding: '1rem',
                  }}
                >
                  <FormattedMessage id="xray_generator.warning.early_cancel.bad_xray_quality" />
                </Message.Header>
              </Message>
            </Modal>
            <Modal open={xRayGeneratorState.warning === GENERATOR_WARNING.X_RAY_TUBE_NOT_READY}>
              <Message negative>
                <Message.Header
                  style={{
                    fontSize: '2em',
                    lineHeight: '1.2em',
                    padding: '1rem',
                  }}
                >
                  <FormattedMessage id="xray_generator.status.warning.X_RAY_TUBE_NOT_READY" />
                </Message.Header>
              </Message>
            </Modal>
          </XRayGeneratorFaultsHistoryContext.Provider>
        </XRayGeneratorStateContext.Provider>
      </XRayGeneratorDispatchContext.Provider>
    </XRayGeneratorControllerContext.Provider>
  );
}

export default memo(XRayGeneratorProvider);
